import { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../utils/api'
import Charts from './Charts'

function Dashboard() {
  const { getToken } = useAuth()
  const [summary, setSummary] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [recentTransactions, setRecentTransactions] = useState([])
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [selectedMonth])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      // Load summary
      const summaryUrl = selectedMonth 
        ? `/api/summary?month=${selectedMonth}`
        : '/api/summary'
      const summaryData = await api.get(summaryUrl, getToken)
      setSummary(summaryData)

      // Load recent expenses
      const expensesData = await api.get('/api/expenses', getToken)
      setRecentTransactions(expensesData.slice(0, 10))

      // Load goals
      const goalsData = await api.get('/api/goals', getToken)
      setGoals(goalsData)
    } catch (error) {
      console.error('Error loading dashboard:', error)
    }
    setLoading(false)
  }

  const getCurrentMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Month Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Financial Overview</h2>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Time</option>
            <option value={getCurrentMonth()}>This Month</option>
            {/* Add more month options as needed */}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SummaryCard
          title="Total Income"
          amount={summary?.totalIncome || 0}
          icon="💵"
          color="green"
        />
        <SummaryCard
          title="Total Expenses"
          amount={summary?.totalExpenses || 0}
          icon="💸"
          color="red"
        />
        <SummaryCard
          title="Available Savings"
          amount={summary?.potentialSavings || 0}
          icon="💰"
          color={summary?.potentialSavings >= 0 ? 'blue' : 'red'}
        />
      </div>

      {/* Charts */}
      {summary?.categoryBreakdown && summary.categoryBreakdown.length > 0 && (
        <Charts categoryData={summary.categoryBreakdown} />
      )}

      {/* Savings Goals Progress */}
      {goals && goals.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Goals</h3>
          <div className="space-y-4">
            {goals.slice(0, 3).map((goal) => (
              <GoalProgress key={goal.id} goal={goal} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {recentTransactions && recentTransactions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Expenses</h3>
          <div className="space-y-3">
            {recentTransactions.map((transaction) => (
              <div key={transaction.id} className="flex justify-between items-center py-2 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{transaction.category_icon}</span>
                  <div>
                    <div className="font-medium text-gray-900">{transaction.description || transaction.category_name}</div>
                    <div className="text-sm text-gray-500">{new Date(transaction.date).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-red-600">-${transaction.amount.toFixed(2)}</div>
                  {transaction.subcategory_name && (
                    <div className="text-xs text-gray-500">{transaction.subcategory_name}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryCard({ title, amount, icon, color }) {
  const colorClasses = {
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200'
  }

  return (
    <div className={`rounded-lg shadow-sm p-6 border-2 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-3xl font-bold mt-2">${amount.toFixed(2)}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  )
}

function GoalProgress({ goal }) {
  const progress = (goal.current_amount / goal.target_amount) * 100
  const remaining = goal.target_amount - goal.current_amount

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="font-medium text-gray-900">{goal.name}</span>
        <span className="text-sm text-gray-600">
          ${goal.current_amount.toFixed(2)} / ${goal.target_amount.toFixed(2)}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className="bg-primary-500 h-3 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(progress, 100)}%` }}
        ></div>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {progress.toFixed(1)}% complete • ${remaining.toFixed(2)} remaining
      </div>
    </div>
  )
}

export default Dashboard

